import { z } from "zod";
import { E2EE_HPKE_SUITE, e2eeCiphertextSchema, e2eeHpkeEnvelopeSchema, e2eeKeyDescriptorSchema, e2eePublicKeySchema, e2eeSignatureSchema } from "./e2eeSchemas.js";
export const roleSchema = z.enum(["admin", "operator", "viewer"]);
export const runStatusSchema = z.enum([
    "queued",
    "waiting_approval",
    "running",
    "finished",
    "error",
    "cancelled"
]);
export const runProgressKindSchema = z.enum(["working", "thinking", "tool", "responding"]);
export const originSchema = z.enum(["web", "telegram", "automation"]);
export const workspaceSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    path: z.string().min(1),
    writable: z.boolean().default(false)
});
export const principalSchema = z.object({
    id: z.string().min(1),
    email: z.string().email().optional(),
    telegramUserId: z.string().optional(),
    displayName: z.string().optional(),
    role: roleSchema
});
export const createRunSchema = z.object({
    origin: z.literal("web"),
    prompt: z.string().min(1),
    conversationId: z.string().uuid().optional(),
    model: z.string().min(1),
    workspaceId: z.string().min(1),
    memoryEnabled: z.boolean().default(true),
    allowWrites: z.boolean().default(false)
});
export const automationCreateRunSchema = z
    .object({
    threadKey: z.string().trim().min(1).max(256),
    title: z.string().trim().min(1).max(200).optional(),
    prompt: z.string().trim().min(1),
    model: z.string().trim().min(1),
    workspaceId: z.string().trim().min(1),
    idempotencyKey: z.string().trim().min(1).max(256),
    allowWrites: z.boolean().default(false)
})
    .strict();
export const conversationSchema = z.object({
    id: z.string().uuid(),
    workspaceId: z.string().min(1),
    title: z.string().nullable(),
    runCount: z.number().int().nonnegative(),
    lastRunAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string()
});
export const runRecordSchema = z.object({
    id: z.string().uuid(),
    conversationId: z.string().uuid(),
    origin: originSchema,
    status: runStatusSchema,
    model: z.string(),
    workspaceId: z.string(),
    prompt: z.string(),
    response: z.string().nullable(),
    error: z.string().nullable(),
    progress: z.string().nullable(),
    progressKind: runProgressKindSchema.nullable(),
    allowWrites: z.boolean(),
    idempotencyKey: z.string().nullable(),
    inputTokens: z.number().int().nonnegative().nullable(),
    outputTokens: z.number().int().nonnegative().nullable(),
    createdAt: z.string(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    updatedAt: z.string()
});
export const conversationTurnSchema = z.object({
    prompt: z.string(),
    response: z.string()
});
export const runnerJobSchema = z.object({
    runId: z.string().uuid(),
    conversationId: z.string().uuid(),
    agentId: z.string().nullable(),
    model: z.string().min(1),
    prompt: z.string().min(1),
    workspace: workspaceSchema,
    userIdentity: z.string().optional(),
    memory: z.array(z.string()).default([]),
    history: z.array(conversationTurnSchema).default([]),
    allowWrites: z.boolean()
});
export const runnerClaimedJobSchema = runnerJobSchema
    .extend({
    leaseId: z.string().uuid()
})
    .strict();
export const runnerJobResultSchema = z.object({
    runId: z.string().uuid(),
    status: z.enum(["finished", "error", "cancelled"]),
    response: z.string().nullable().default(null),
    error: z.string().nullable().default(null),
    agentId: z.string().nullable().default(null),
    inputTokens: z.number().int().nonnegative().nullable().default(null),
    outputTokens: z.number().int().nonnegative().nullable().default(null)
});
export const runnerJobProgressSchema = z.object({
    runId: z.string().uuid(),
    kind: runProgressKindSchema,
    message: z.string().trim().min(1).max(200_000)
});
export const modelSchema = z.object({
    id: z.string(),
    displayName: z.string().optional()
});
export const reportIdSchema = z.enum([
    "finance",
    "news",
    "ai-infra-tips",
    "ai-infra-interview",
    "ai-infra-mianshi"
]);
export const reportDefinitionSchema = z.object({
    id: reportIdSchema,
    name: z.string(),
    shortName: z.string(),
    description: z.string(),
    schedule: z.string(),
    threadKey: z.string()
});
export const reportQuestionSchema = z
    .object({
    question: z.string().trim().min(1).max(8_000),
    requestId: z.string().uuid()
})
    .strict();
export const memoryFactSchema = z.object({
    id: z.string().uuid(),
    scope: z.enum(["user", "workspace"]),
    workspaceId: z.string().nullable(),
    content: z.string().min(1),
    createdAt: z.string(),
    updatedAt: z.string()
});
export const E2EE_PROTOCOL = "cg-e2ee/1";
export { E2EE_HPKE_SUITE, e2eeCiphertextSchema, e2eeHpkeEnvelopeSchema, e2eeKeyDescriptorSchema, e2eePublicKeySchema, e2eeSignatureSchema };
const base64UrlSchema = (maxLength) => z.string().min(1).max(maxLength).regex(/^[A-Za-z0-9_-]+$/);
export const publicWorkspaceSchema = z
    .object({
    id: z.string().min(1).max(256),
    label: z.string().min(1).max(256),
    writable: z.boolean()
})
    .strict();
export const e2eeRunnerCapabilitySchema = z
    .object({
    protocols: z.array(z.literal(E2EE_PROTOCOL)).min(1).max(4),
    encryptionKey: e2eeKeyDescriptorSchema,
    signingKey: e2eeKeyDescriptorSchema
})
    .strict();
export const e2eeRunRoutingSchema = z
    .object({
    model: z.string().trim().min(1).max(256),
    workspaceId: z.string().trim().min(1).max(256),
    allowWrites: z.boolean(),
    memoryEnabled: z.boolean()
})
    .strict();
export const e2eeConversationTurnSchema = z
    .object({
    prompt: z.string().max(200_000),
    response: z.string().max(1_000_000)
})
    .strict();
export const e2eeRunPayloadSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    kind: z.literal("run-request"),
    messageId: z.string().uuid(),
    runId: z.string().uuid(),
    conversationId: z.string().uuid(),
    sequence: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    routing: e2eeRunRoutingSchema,
    prompt: z.string().trim().min(1).max(500_000),
    history: z.array(e2eeConversationTurnSchema).max(50).default([]),
    memory: z.array(z.string().max(100_000)).max(200).default([]),
    userIdentity: z.string().max(512).optional(),
    previousDigest: base64UrlSchema(43).length(43).nullable()
})
    .strict();
export const e2eeRunRequestEnvelopeSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    kind: z.literal("run-request"),
    messageId: z.string().uuid(),
    runId: z.string().uuid(),
    conversationId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    clientKeyId: z.string().trim().min(8).max(128),
    runnerId: z.string().trim().min(1).max(128),
    runnerKeyId: z.string().trim().min(8).max(128),
    sequence: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    createdAt: z.string().min(1).max(64),
    routing: e2eeRunRoutingSchema,
    previousDigest: base64UrlSchema(43).length(43).nullable(),
    wrappedConversationKey: e2eeHpkeEnvelopeSchema,
    title: e2eeCiphertextSchema.nullable(),
    payload: e2eeCiphertextSchema,
    signature: e2eeSignatureSchema
})
    .strict();
export const e2eeApprovalEnvelopeSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    kind: z.literal("run-approval"),
    messageId: z.string().uuid(),
    runId: z.string().uuid(),
    conversationId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    clientKeyId: z.string().trim().min(8).max(128),
    runnerId: z.string().trim().min(1).max(128),
    runnerKeyId: z.string().trim().min(8).max(128),
    requestDigest: base64UrlSchema(43).length(43),
    allowWrites: z.literal(true),
    createdAt: z.string().min(1).max(64),
    signature: e2eeSignatureSchema
})
    .strict();
export const e2eeProgressPayloadSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    kind: z.literal("run-progress"),
    runId: z.string().uuid(),
    conversationId: z.string().uuid(),
    sequence: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    progressKind: runProgressKindSchema,
    message: z.string().trim().min(1).max(200_000)
})
    .strict();
export const e2eeProgressEnvelopeSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    kind: z.literal("run-progress"),
    messageId: z.string().uuid(),
    runId: z.string().uuid(),
    conversationId: z.string().uuid(),
    runnerId: z.string().trim().min(1).max(128),
    runnerKeyId: z.string().trim().min(8).max(128),
    requestDigest: base64UrlSchema(43).length(43),
    sequence: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    progressKind: runProgressKindSchema,
    createdAt: z.string().min(1).max(64),
    payload: e2eeCiphertextSchema,
    signature: e2eeSignatureSchema
})
    .strict();
export const e2eeResultStatusSchema = z.enum(["finished", "error", "cancelled"]);
export const e2eeResultPayloadSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    kind: z.literal("run-result"),
    runId: z.string().uuid(),
    conversationId: z.string().uuid(),
    status: e2eeResultStatusSchema,
    response: z.string().max(2_000_000).nullable(),
    error: z.string().max(200_000).nullable(),
    inputTokens: z.number().int().nonnegative().nullable(),
    outputTokens: z.number().int().nonnegative().nullable()
})
    .strict();
export const e2eeResultEnvelopeSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    kind: z.literal("run-result"),
    messageId: z.string().uuid(),
    runId: z.string().uuid(),
    conversationId: z.string().uuid(),
    runnerId: z.string().trim().min(1).max(128),
    runnerKeyId: z.string().trim().min(8).max(128),
    requestDigest: base64UrlSchema(43).length(43),
    sequence: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    status: e2eeResultStatusSchema,
    createdAt: z.string().min(1).max(64),
    payload: e2eeCiphertextSchema,
    signature: e2eeSignatureSchema
})
    .strict();
export const e2eeMemoryPayloadSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    kind: z.literal("memory"),
    memoryId: z.string().uuid(),
    scope: z.enum(["user", "workspace"]),
    workspaceId: z.string().min(1).max(256).nullable(),
    content: z.string().trim().min(1).max(100_000)
})
    .strict();
export const e2eeMemoryEnvelopeSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    kind: z.literal("memory"),
    messageId: z.string().uuid(),
    memoryId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    clientKeyId: z.string().trim().min(8).max(128),
    scope: z.enum(["user", "workspace"]),
    workspaceId: z.string().min(1).max(256).nullable(),
    createdAt: z.string().min(1).max(64),
    payload: e2eeCiphertextSchema,
    signature: e2eeSignatureSchema
})
    .strict();
export const e2eeRunnerJobSchema = z
    .object({
    contentMode: z.literal("e2ee-v1"),
    leaseId: z.string().uuid(),
    leaseExpiresAt: z.string().min(1).max(64),
    request: e2eeRunRequestEnvelopeSchema,
    approval: e2eeApprovalEnvelopeSchema.nullable()
})
    .strict();
export const e2eeProgressSubmissionSchema = z
    .object({
    leaseId: z.string().uuid(),
    envelope: e2eeProgressEnvelopeSchema
})
    .strict();
export const e2eeResultSubmissionSchema = z
    .object({
    leaseId: z.string().uuid(),
    envelope: e2eeResultEnvelopeSchema
})
    .strict();
export const e2eeRunRejectionCodeSchema = z.enum([
    "client_not_paired",
    "invalid_request",
    "runner_state_mismatch",
    "processor_failed"
]);
export const e2eeRunRejectionSchema = z
    .object({
    runnerId: z.string().trim().min(1).max(128),
    runnerKeyId: z.string().trim().min(8).max(128),
    leaseId: z.string().uuid(),
    code: e2eeRunRejectionCodeSchema
})
    .strict();
export const e2eeRunRecordSchema = z
    .object({
    id: z.string().uuid(),
    conversationId: z.string().uuid(),
    status: runStatusSchema,
    model: z.string(),
    workspaceId: z.string(),
    allowWrites: z.boolean(),
    request: e2eeRunRequestEnvelopeSchema,
    approval: e2eeApprovalEnvelopeSchema.nullable(),
    progress: e2eeProgressEnvelopeSchema.nullable(),
    result: e2eeResultEnvelopeSchema.nullable(),
    createdAt: z.string(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    updatedAt: z.string()
})
    .strict();
export const e2eeConversationRecordSchema = z
    .object({
    id: z.string().uuid(),
    workspaceId: z.string(),
    runnerId: z.string(),
    runnerKeyId: z.string(),
    title: e2eeCiphertextSchema.nullable(),
    runCount: z.number().int().nonnegative(),
    lastRunAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string()
})
    .strict();
export const e2eeCreateRunRequestSchema = z
    .object({
    request: e2eeRunRequestEnvelopeSchema
})
    .strict();
export const e2eeApprovalSubmissionSchema = z
    .object({
    approval: e2eeApprovalEnvelopeSchema
})
    .strict();
export const e2eeRunnerClaimRequestSchema = z
    .object({
    runnerId: z.string().trim().min(1).max(128),
    runnerKeyId: z.string().trim().min(8).max(128),
    protocols: z.array(z.literal(E2EE_PROTOCOL)).min(1).max(4)
})
    .strict();
export const e2eeLeaseRenewalSchema = z
    .object({
    runnerId: z.string().trim().min(1).max(128),
    runnerKeyId: z.string().trim().min(8).max(128),
    leaseId: z.string().uuid()
})
    .strict();
export const e2eeRunnerHeartbeatSchema = z
    .object({
    runnerId: z.string().trim().min(1).max(128),
    runnerVersion: z.string().trim().min(1).max(64),
    models: z.array(modelSchema).max(256),
    workspaces: z.array(publicWorkspaceSchema).max(256),
    e2ee: e2eeRunnerCapabilitySchema
})
    .strict();
export const e2eeRunnerDirectoryEntrySchema = e2eeRunnerHeartbeatSchema
    .extend({
    lastSeenAt: z.string(),
    online: z.boolean()
})
    .strict();
export const e2eeMemoryCreateRequestSchema = z
    .object({
    envelope: e2eeMemoryEnvelopeSchema
})
    .strict();
export const e2eeMemoryRecordSchema = z
    .object({
    id: z.string().uuid(),
    scope: z.enum(["user", "workspace"]),
    workspaceId: z.string().nullable(),
    envelope: e2eeMemoryEnvelopeSchema,
    createdAt: z.string(),
    updatedAt: z.string()
})
    .strict();
export const e2eeRunnerPairingBundleSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    kind: z.literal("runner-pairing"),
    runnerId: z.string().trim().min(1).max(128),
    encryptionKey: e2eeKeyDescriptorSchema,
    signingKey: e2eeKeyDescriptorSchema,
    createdAt: z.string().min(1).max(64)
})
    .strict();
export const e2eeClientPairingBundleSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    kind: z.literal("client-pairing"),
    clientId: z.string().trim().min(8).max(128),
    signingKey: e2eeKeyDescriptorSchema,
    // Optional for legacy Chrome extension offline pairing; required for secure-web.
    encryptionKey: e2eeKeyDescriptorSchema.optional(),
    createdAt: z.string().min(1).max(64)
})
    .strict();
/** Magic-link pairing protocol kinds (MVP secure-web). */
export const E2EE_PAIRING_KIND = "secure-web-magic-link/1";
export const e2eePairingStartSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    pairingKind: z.literal(E2EE_PAIRING_KIND),
    pairId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    clientChallenge: base64UrlSchema(43).length(43),
    signingKey: e2eeKeyDescriptorSchema,
    encryptionKey: e2eeKeyDescriptorSchema,
    secureOrigin: z.string().url().max(512),
    gatewayOrigin: z.string().url().max(512),
    createdAt: z.string().min(1).max(64)
})
    .strict();
export const e2eePairingOfferSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    pairingKind: z.literal(E2EE_PAIRING_KIND),
    pairId: z.string().uuid(),
    runnerId: z.string().trim().min(1).max(128),
    runnerChallenge: base64UrlSchema(43).length(43),
    runnerEncryptionKey: e2eeKeyDescriptorSchema,
    runnerSigningKey: e2eeKeyDescriptorSchema,
    clientId: z.string().trim().min(8).max(128),
    clientChallenge: base64UrlSchema(43).length(43),
    clientSigningFingerprint: z.string().regex(/^sha256:[A-Za-z0-9_-]{43}$/),
    clientEncryptionFingerprint: z.string().regex(/^sha256:[A-Za-z0-9_-]{43}$/),
    secureOrigin: z.string().url().max(512),
    gatewayOrigin: z.string().url().max(512),
    emailHint: z
        .string()
        .trim()
        .min(3)
        .max(320)
        .refine((value) => !/[\r\n\0]/.test(value), "email_hint_injection")
        .optional(),
    expiresAt: z.string().min(1).max(64),
    createdAt: z.string().min(1).max(64)
})
    .strict();
export const e2eePairingCompleteSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    pairingKind: z.literal(E2EE_PAIRING_KIND),
    pairId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    transcriptMac: base64UrlSchema(43).length(43),
    signature: e2eeSignatureSchema,
    createdAt: z.string().min(1).max(64)
})
    .strict();
export const e2eePairingAckSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    pairingKind: z.literal(E2EE_PAIRING_KIND),
    pairId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    runnerId: z.string().trim().min(1).max(128),
    status: z.enum(["paired", "rejected"]),
    runnerEncryptionKey: e2eeKeyDescriptorSchema,
    runnerSigningKey: e2eeKeyDescriptorSchema,
    createdAt: z.string().min(1).max(64),
    signature: e2eeSignatureSchema
})
    .strict();
export const e2eePairingStatusSchema = z.enum([
    "pending_start",
    "offer_ready",
    "complete_submitted",
    "paired",
    "rejected",
    "expired"
]);
export const e2eeDeviceRecordSchema = z
    .object({
    clientId: z.string().trim().min(8).max(128),
    signingKey: e2eeKeyDescriptorSchema,
    encryptionKey: e2eeKeyDescriptorSchema.nullable(),
    pairedAt: z.string().min(1).max(64),
    label: z.string().max(128).nullable(),
    revokedAt: z.string().min(1).max(64).nullable()
})
    .strict();
/** Runner rewraps conversation root for a newly paired device (no private-key sync). */
export const e2eeKeyGrantSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    kind: z.literal("conversation-key-grant"),
    grantId: z.string().uuid(),
    conversationId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    runnerId: z.string().trim().min(1).max(128),
    runnerKeyId: z.string().trim().min(8).max(128),
    wrappedConversationKey: e2eeHpkeEnvelopeSchema,
    createdAt: z.string().min(1).max(64),
    signature: e2eeSignatureSchema
})
    .strict();
/** Offline ECDSA P-256 trust root that signs Runner identity certificates. */
export const E2EE_TRUST_ROOT_KIND = "trust-root-public/1";
export const E2EE_RUNNER_CERT_KIND = "runner-identity-cert/1";
export const E2EE_PASSKEY_PAIRING_KIND = "secure-web-passkey/1";
export const E2EE_DEVICE_APPROVAL_KIND = "paired-device-approval/1";
export const E2EE_RECOVERY_PAIRING_KIND = "secure-web-recovery/1";
export const e2eeTrustRootPublicSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    kind: z.literal(E2EE_TRUST_ROOT_KIND),
    keyId: z.string().trim().min(8).max(128),
    fingerprint: z.string().regex(/^sha256:[A-Za-z0-9_-]{43}$/),
    publicKey: e2eePublicKeySchema,
    epoch: z.number().int().nonnegative().max(1_000_000),
    createdAt: z.string().min(1).max(64)
})
    .strict();
export const e2eeRunnerIdentityCertSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    kind: z.literal(E2EE_RUNNER_CERT_KIND),
    version: z.literal(1),
    certId: z.string().uuid(),
    runnerId: z.string().trim().min(1).max(128),
    epoch: z.number().int().nonnegative().max(1_000_000),
    encryptionKey: e2eeKeyDescriptorSchema,
    signingKey: e2eeKeyDescriptorSchema,
    allowedSecureOrigins: z.array(z.string().url().max(512)).min(1).max(16),
    allowedRpIds: z.array(z.string().trim().min(1).max(253)).min(1).max(16),
    issuedAt: z.string().min(1).max(64),
    expiresAt: z.string().min(1).max(64),
    rootKeyId: z.string().trim().min(8).max(128),
    rootFingerprint: z.string().regex(/^sha256:[A-Za-z0-9_-]{43}$/),
    signature: e2eeSignatureSchema
})
    .strict();
/**
 * CS → Secure redirect authorization: Runner signs a one-time grant for a CS
 * device public key (private key never leaves cs origin).
 */
export const E2EE_CS_AUTH_KIND = "cs-web-device-auth/1";
export const e2eeCsAuthStatusSchema = z.enum([
    "intent_ready",
    "pending_runner",
    "granted",
    "rejected",
    "consumed",
    "expired"
]);
export const e2eeCsAuthIntentSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    authKind: z.literal(E2EE_CS_AUTH_KIND),
    authId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    challenge: base64UrlSchema(43).length(43),
    state: base64UrlSchema(43).length(43),
    signingKey: e2eeKeyDescriptorSchema,
    encryptionKey: e2eeKeyDescriptorSchema,
    returnOrigin: z.string().url().max(512),
    gatewayOrigin: z.string().url().max(512),
    createdAt: z.string().min(1).max(64)
})
    .strict();
export const e2eeCsAuthGrantSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    authKind: z.literal(E2EE_CS_AUTH_KIND),
    authId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    challenge: base64UrlSchema(43).length(43),
    state: base64UrlSchema(43).length(43),
    signingFingerprint: z.string().regex(/^sha256:[A-Za-z0-9_-]{43}$/),
    encryptionFingerprint: z.string().regex(/^sha256:[A-Za-z0-9_-]{43}$/),
    returnOrigin: z.string().url().max(512),
    gatewayOrigin: z.string().url().max(512),
    runnerId: z.string().trim().min(1).max(128),
    runnerEncryptionKey: e2eeKeyDescriptorSchema,
    runnerSigningKey: e2eeKeyDescriptorSchema,
    runnerCertificate: e2eeRunnerIdentityCertSchema.optional(),
    status: z.enum(["authorized", "rejected"]),
    expiresAt: z.string().min(1).max(64),
    createdAt: z.string().min(1).max(64),
    signature: e2eeSignatureSchema
})
    .strict();
export const e2eeCsAuthIntentRequestSchema = z
    .object({
    intent: e2eeCsAuthIntentSchema
})
    .strict();
export const e2eePairingStartRequestSchema = z
    .object({
    start: e2eePairingStartSchema
})
    .strict();
export const e2eePairingCompleteRequestSchema = z
    .object({
    complete: e2eePairingCompleteSchema
})
    .strict();
export const e2eePasskeyCredentialPublicSchema = z
    .object({
    credentialId: z.string().trim().min(16).max(512),
    publicKey: z.string().trim().min(16).max(8_192),
    counter: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    transports: z.array(z.string().trim().min(1).max(32)).max(16).optional(),
    label: z.string().trim().min(1).max(128).nullable(),
    createdAt: z.string().min(1).max(64),
    revokedAt: z.string().min(1).max(64).nullable()
})
    .strict();
export const e2eePasskeyPairingStartSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    pairingKind: z.literal(E2EE_PASSKEY_PAIRING_KIND),
    pairId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    clientChallenge: base64UrlSchema(43).length(43),
    signingKey: e2eeKeyDescriptorSchema,
    encryptionKey: e2eeKeyDescriptorSchema,
    secureOrigin: z.string().url().max(512),
    gatewayOrigin: z.string().url().max(512),
    createdAt: z.string().min(1).max(64)
})
    .strict();
export const e2eePasskeyPairingOptionsSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    pairingKind: z.literal(E2EE_PASSKEY_PAIRING_KIND),
    pairId: z.string().uuid(),
    runnerId: z.string().trim().min(1).max(128),
    mode: z.enum(["registration", "authentication"]),
    options: z.record(z.string(), z.unknown()),
    runnerEncryptionKey: e2eeKeyDescriptorSchema,
    runnerSigningKey: e2eeKeyDescriptorSchema,
    runnerCertificate: e2eeRunnerIdentityCertSchema,
    clientId: z.string().trim().min(8).max(128),
    clientChallenge: base64UrlSchema(43).length(43),
    clientSigningFingerprint: z.string().regex(/^sha256:[A-Za-z0-9_-]{43}$/),
    clientEncryptionFingerprint: z.string().regex(/^sha256:[A-Za-z0-9_-]{43}$/),
    secureOrigin: z.string().url().max(512),
    gatewayOrigin: z.string().url().max(512),
    expiresAt: z.string().min(1).max(64),
    createdAt: z.string().min(1).max(64)
})
    .strict();
export const e2eePasskeyPairingCompleteSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    pairingKind: z.literal(E2EE_PASSKEY_PAIRING_KIND),
    pairId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    mode: z.enum(["registration", "authentication"]),
    response: z.record(z.string(), z.unknown()),
    signature: e2eeSignatureSchema,
    createdAt: z.string().min(1).max(64)
})
    .strict();
export const e2eePasskeyPairingAckSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    pairingKind: z.literal(E2EE_PASSKEY_PAIRING_KIND),
    pairId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    runnerId: z.string().trim().min(1).max(128),
    status: z.enum(["paired", "rejected"]),
    /** Safe machine-readable reject code (no secrets); omitted on success. */
    reason: z
        .string()
        .trim()
        .regex(/^[a-z][a-z0-9_]{1,127}$/)
        .optional(),
    runnerEncryptionKey: e2eeKeyDescriptorSchema,
    runnerSigningKey: e2eeKeyDescriptorSchema,
    runnerCertificate: e2eeRunnerIdentityCertSchema,
    createdAt: z.string().min(1).max(64),
    signature: e2eeSignatureSchema
})
    .strict();
export const e2eeDeviceApprovalRequestSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    approvalKind: z.literal(E2EE_DEVICE_APPROVAL_KIND),
    approvalId: z.string().uuid(),
    newClientId: z.string().trim().min(8).max(128),
    newSigningFingerprint: z.string().regex(/^sha256:[A-Za-z0-9_-]{43}$/),
    newEncryptionFingerprint: z.string().regex(/^sha256:[A-Za-z0-9_-]{43}$/),
    newSigningKey: e2eeKeyDescriptorSchema,
    newEncryptionKey: e2eeKeyDescriptorSchema,
    secureOrigin: z.string().url().max(512),
    gatewayOrigin: z.string().url().max(512),
    label: z.string().trim().min(1).max(128).nullable().optional(),
    expiresAt: z.string().min(1).max(64),
    createdAt: z.string().min(1).max(64)
})
    .strict();
export const e2eeDeviceApprovalDecisionSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    approvalKind: z.literal(E2EE_DEVICE_APPROVAL_KIND),
    approvalId: z.string().uuid(),
    approverClientId: z.string().trim().min(8).max(128),
    decision: z.enum(["approved", "rejected"]),
    createdAt: z.string().min(1).max(64),
    signature: e2eeSignatureSchema
})
    .strict();
export const e2eeRecoveryPairingStartSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    pairingKind: z.literal(E2EE_RECOVERY_PAIRING_KIND),
    pairId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    clientChallenge: base64UrlSchema(43).length(43),
    signingKey: e2eeKeyDescriptorSchema,
    encryptionKey: e2eeKeyDescriptorSchema,
    secureOrigin: z.string().url().max(512),
    gatewayOrigin: z.string().url().max(512),
    createdAt: z.string().min(1).max(64)
})
    .strict();
export const e2eeRecoveryPairingOfferSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    pairingKind: z.literal(E2EE_RECOVERY_PAIRING_KIND),
    pairId: z.string().uuid(),
    runnerId: z.string().trim().min(1).max(128),
    runnerChallenge: base64UrlSchema(43).length(43),
    runnerEncryptionKey: e2eeKeyDescriptorSchema,
    runnerSigningKey: e2eeKeyDescriptorSchema,
    runnerCertificate: e2eeRunnerIdentityCertSchema,
    clientId: z.string().trim().min(8).max(128),
    clientChallenge: base64UrlSchema(43).length(43),
    clientSigningFingerprint: z.string().regex(/^sha256:[A-Za-z0-9_-]{43}$/),
    clientEncryptionFingerprint: z.string().regex(/^sha256:[A-Za-z0-9_-]{43}$/),
    secureOrigin: z.string().url().max(512),
    gatewayOrigin: z.string().url().max(512),
    expiresAt: z.string().min(1).max(64),
    createdAt: z.string().min(1).max(64)
})
    .strict();
export const e2eeRecoveryPairingCompleteSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    pairingKind: z.literal(E2EE_RECOVERY_PAIRING_KIND),
    pairId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    transcriptMac: base64UrlSchema(43).length(43),
    signature: e2eeSignatureSchema,
    createdAt: z.string().min(1).max(64)
})
    .strict();
export const e2eeRecoveryPairingAckSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    pairingKind: z.literal(E2EE_RECOVERY_PAIRING_KIND),
    pairId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    runnerId: z.string().trim().min(1).max(128),
    status: z.enum(["paired", "rejected"]),
    runnerEncryptionKey: e2eeKeyDescriptorSchema,
    runnerSigningKey: e2eeKeyDescriptorSchema,
    runnerCertificate: e2eeRunnerIdentityCertSchema,
    createdAt: z.string().min(1).max(64),
    signature: e2eeSignatureSchema
})
    .strict();
/** Runner-signed final outcome of a paired-device-approval decision. */
export const e2eeDeviceApprovalResultSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    approvalKind: z.literal(E2EE_DEVICE_APPROVAL_KIND),
    approvalId: z.string().uuid(),
    newClientId: z.string().trim().min(8).max(128),
    runnerId: z.string().trim().min(1).max(128),
    status: z.enum(["paired", "rejected"]),
    runnerEncryptionKey: e2eeKeyDescriptorSchema,
    runnerSigningKey: e2eeKeyDescriptorSchema,
    runnerCertificate: e2eeRunnerIdentityCertSchema,
    createdAt: z.string().min(1).max(64),
    signature: e2eeSignatureSchema
})
    .strict();
export const e2eePasskeyPairingStartRequestSchema = z
    .object({ start: e2eePasskeyPairingStartSchema })
    .strict();
export const e2eePasskeyPairingCompleteRequestSchema = z
    .object({ complete: e2eePasskeyPairingCompleteSchema })
    .strict();
export const e2eeDeviceApprovalRequestBodySchema = z
    .object({ request: e2eeDeviceApprovalRequestSchema })
    .strict();
export const e2eeDeviceApprovalDecisionBodySchema = z
    .object({ decision: e2eeDeviceApprovalDecisionSchema })
    .strict();
export const e2eeRecoveryPairingStartRequestSchema = z
    .object({ start: e2eeRecoveryPairingStartSchema })
    .strict();
export const e2eeRecoveryPairingCompleteRequestSchema = z
    .object({
    complete: e2eeRecoveryPairingCompleteSchema
})
    .strict();
/** Public recovery handle advertised by Runner CLI (secret stays in URL fragment). */
export const e2eeRecoveryHandleSchema = z
    .object({
    recoveryId: z.string().uuid(),
    expiresAt: z.string().min(1).max(64)
})
    .strict();
/* -------------------------------------------------------------------------- */
/* Runner-assisted manual code (RAMC) pairing — secure-web-runner-code/1.      */
/*                                                                             */
/* Primary no-QR / no-email device verification. The Runner generates a live   */
/* 128-bit one-time code, shows it (plus a 6-word SAS) on its own terminal /   */
/* TTY; the operator types the code into the browser. The code is NEVER        */
/* uploaded and NEVER stored server-side in cleartext — only public envelopes  */
/* + an HMAC transcript tag cross the wire. Roles are the mirror image of      */
/* recovery pairing: here the *Runner* holds the secret and the *browser*      */
/* proves knowledge by MAC-ing the transcript with the typed code.            */
/* -------------------------------------------------------------------------- */
export const E2EE_RUNNER_CODE_PAIRING_KIND = "secure-web-runner-code/1";
export const e2eeRunnerCodeStatusSchema = z.enum([
    "requested",
    "offered",
    "confirm_submitted",
    "paired",
    "cert_issued",
    "locked",
    "expired",
    "rejected"
]);
/** Browser → Gateway: begin an enrollment. accountId/email come from CF Access, never here. */
export const e2eeRunnerCodePairingStartSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    pairingKind: z.literal(E2EE_RUNNER_CODE_PAIRING_KIND),
    enrollId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    clientChallenge: base64UrlSchema(43).length(43),
    signingKey: e2eeKeyDescriptorSchema,
    encryptionKey: e2eeKeyDescriptorSchema,
    label: z.string().trim().min(1).max(128).nullable().optional(),
    secureOrigin: z.string().url().max(512),
    gatewayOrigin: z.string().url().max(512),
    createdAt: z.string().min(1).max(64)
})
    .strict();
/** Runner → Gateway → Browser: publish the enrollment offer (public material only). */
export const e2eeRunnerCodePairingOfferSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    pairingKind: z.literal(E2EE_RUNNER_CODE_PAIRING_KIND),
    enrollId: z.string().uuid(),
    runnerId: z.string().trim().min(1).max(128),
    /** Server/Runner freshness nonce bound into the transcript. */
    serverNonce: base64UrlSchema(43).length(43),
    runnerChallenge: base64UrlSchema(43).length(43),
    runnerEncryptionKey: e2eeKeyDescriptorSchema,
    runnerSigningKey: e2eeKeyDescriptorSchema,
    runnerCertificate: e2eeRunnerIdentityCertSchema,
    clientId: z.string().trim().min(8).max(128),
    clientChallenge: base64UrlSchema(43).length(43),
    clientSigningFingerprint: z.string().regex(/^sha256:[A-Za-z0-9_-]{43}$/),
    clientEncryptionFingerprint: z.string().regex(/^sha256:[A-Za-z0-9_-]{43}$/),
    secureOrigin: z.string().url().max(512),
    gatewayOrigin: z.string().url().max(512),
    expiresAt: z.string().min(1).max(64),
    createdAt: z.string().min(1).max(64)
})
    .strict();
/** Browser → Gateway → Runner: prove knowledge of the typed code via HMAC transcript tag. */
export const e2eeRunnerCodePairingConfirmSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    pairingKind: z.literal(E2EE_RUNNER_CODE_PAIRING_KIND),
    enrollId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    /** HMAC(code, transcript) — the code itself never crosses the wire. */
    transcriptMac: base64UrlSchema(43).length(43),
    /** 6-word SAS the browser displayed, for the operator to compare (P2 mode B). */
    sas: z.array(z.string().trim().min(1).max(24)).length(6),
    signature: e2eeSignatureSchema,
    createdAt: z.string().min(1).max(64)
})
    .strict();
/** Runner → Gateway → Browser: final signed outcome; carries the account-bound cg cert when issued. */
export const e2eeRunnerCodePairingAckSchema = z
    .object({
    protocol: z.literal(E2EE_PROTOCOL),
    pairingKind: z.literal(E2EE_RUNNER_CODE_PAIRING_KIND),
    enrollId: z.string().uuid(),
    clientId: z.string().trim().min(8).max(128),
    runnerId: z.string().trim().min(1).max(128),
    status: z.enum(["paired", "rejected"]),
    /** Safe machine-readable reject code (no secrets); omitted on success. */
    reason: z
        .string()
        .trim()
        .regex(/^[a-z][a-z0-9_]{1,127}$/)
        .optional(),
    runnerEncryptionKey: e2eeKeyDescriptorSchema,
    runnerSigningKey: e2eeKeyDescriptorSchema,
    runnerCertificate: e2eeRunnerIdentityCertSchema,
    createdAt: z.string().min(1).max(64),
    signature: e2eeSignatureSchema
})
    .strict();
export const e2eeRunnerCodePairingStartRequestSchema = z
    .object({ start: e2eeRunnerCodePairingStartSchema })
    .strict();
export const e2eeRunnerCodePairingConfirmRequestSchema = z
    .object({ confirm: e2eeRunnerCodePairingConfirmSchema })
    .strict();
export * from "./runPolling.js";
// cg-mitm/1 application-layer anti-MITM channel schema (P1). Re-exported last so the
// base e2ee* schemas above are fully initialized before cgMitm.ts consumes them.
export * from "./cgMitm.js";
//# sourceMappingURL=index.js.map